import { navigationMenuBar } from '@/components/header/headerStructure';
import { JSX } from '@fullcalendar/core/preact.js';
import { store } from '@state/store';
import { PermissionsType } from '@types';

export const isPermission = (
  permissions:
    | PermissionsType.Permission
    | PermissionsType.PermissionsPartial
    | PermissionsType.Permissions,
  userPermissions: PermissionsType.PermissionsPartial,
) => {
  if (Array.isArray(permissions)) {
    return permissions.some((permission) =>
      userPermissions?.includes(permission),
    );
  }
  return userPermissions?.includes(permissions) || false;
};

// Boolean utility function for permission checks
export const checkPermission = async (
  permissions:
    | PermissionsType.Permission
    | PermissionsType.PermissionsPartial
    | PermissionsType.Permissions,
): Promise<boolean> => {
  const state = store.getState();

  const hasInitialised = await waitForCondition(
    () => state.auth?.current.userid !== null,
  );

  if (!hasInitialised) {
    return false;
  }

  if (!state.auth?.current.userid) {
    return false;
  }

  const userPermissions = state.auth?.current.user?.permissions || [];
  return isPermission(permissions, userPermissions);
};

/*
  This is a function that takes in a path and returns the permissions that are required to access that path
  which is provided in navigationMenuBar
  eg: /projects -> [add_project, change_project, delete_project, view_project]
*/
export const getRoutePermissions = (path: string) => {
  const findNode = (nodes: any[]): string[] | undefined => {
    for (const node of nodes) {
      if (node.action === `${path}`) {
        return node.permission;
      }

      if (node.children) {
        const result = findNode(node.children);
        if (result) return result;
      }
    }
  };

  return findNode(navigationMenuBar);
};

export type AllowedTab = {
  label: JSX.Element;
  value: string;
  permissions?: string[];
  testId?: string;
};
/* 
  This function is used to filter out tabs that the user does not have permission to access
  Removes tabs and does not render tabs based on user permissions and tab permissions
  eg :
  const tabsList = [
    {
      label: <HStack>Projects</HStack>,
      value: 'projects',
      permissions: [
        permissionsKeys.add_project,
        permissionsKeys.change_project,
        permissionsKeys.delete_project,
        permissionsKeys.view_project,
      ],
    },
    {
      label: <HStack>Project Sheets</HStack>,
      value: 'projectSheets',
    },
  ];

  - Project tab has required permissions [add_project, change_project, delete_project, view_project]
    to view the project tab
  - Requires any of the permissions to view the project tab
  - Not checking permissions using getRoutePermissions('/projects') because it may have permissions from other tabs
  - Checks if the user has permission to access the tab
  - If the user does not have permission to access the tab, it will remove the tab
*/
export const getAllowedTabs = (tabsList: AllowedTab[]): AllowedTab[] => {
  const state = store.getState();
  const currentUser = state.auth?.current;

  const allowedTabs = tabsList.filter((tab) => {
    if (!tab.permissions || tab.permissions.length === 0) return true;

    return isPermission(tab.permissions, currentUser?.user?.permissions || []);
  });

  return allowedTabs;
};

export const filterArrayByPermission = async (
  items: any[],
): Promise<
  PermissionsType.PermissionsPartial | PermissionsType.Permissions | any
> => {
  const state = store.getState();

  const hasInitialised = await waitForCondition(
    () => state.auth?.current.userid !== null,
  );

  if (!hasInitialised) {
    return [];
  }

  if (!state.auth?.current.userid) {
    return [];
  }

  const userPermissions = state.auth?.current.user?.permissions || [];

  const filterItems = (nodes: any[]): any[] => {
    return (
      nodes
        .map((node) => {
          const hasChildren =
            Array.isArray(node.children) && node.children.length > 0;
          const isNodeAllowed = node.permission
            ? isPermission(node.permission, userPermissions)
            : true;

          if (hasChildren) {
            const filteredChildren = filterItems(node.children);

            // Include this node if:
            // - node itself is allowed, or
            // - any of its children are allowed
            if (isNodeAllowed || filteredChildren.length > 0) {
              return { ...node, children: filteredChildren };
            }
            return null;
          }

          // Leaf node: include only if allowed
          return isNodeAllowed ? node : null;
        })
        /* 
        Filter out null nodes
        and node with no children
        eg:
          {
            value: 'treeHeader-production',
            label: 'Production',
            children: [
                 {
                     value: 'assembly',
                     label: 'Assembly',
                     action: '/assembly',
                 },
                 {
                     value: 'glazing',
                     label: 'Glazing',
                        action: '/glazing',
                  },
                 {
                     value: 'installation',
                     label: 'Installation',
                     action: '/installation',
                 },
            ],
          },

          if the node has no children because of permission,
          it will be filtered out
        */
        .filter(
          (node) => node && !(node.children && node.children?.length === 0),
        )
    );
  };

  return filterItems(items);
};

const waitForCondition = (
  condition: () => boolean,
  interval = 10,
  timeout = 10_000,
  maxRetries = Infinity,
): Promise<boolean> => {
  const start = Date.now();
  let attempts = 0;

  return new Promise((resolve, reject) => {
    const check = () => {
      if (condition()) {
        resolve(true);
      } else if (Date.now() - start >= timeout || ++attempts > maxRetries) {
        reject(false);
      } else {
        setTimeout(check, interval);
      }
    };
    check();
  });
};

export const userPermissionsCheck = async (): Promise<{
  [key in PermissionsType.Permission]: boolean;
}> => {
  const state = store.getState();
  const userPermissions = state.auth?.current.user?.permissions || [];
  const permissionsCheck: { [key in PermissionsType.Permission]: boolean } = {};

  PermissionsType.permissionsList.forEach((permission) => {
    if (!permission) return;
    const key = permission as PermissionsType.Permission;
    permissionsCheck[key] = userPermissions.includes(key);
  });
  return permissionsCheck;
};
